import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import type { MarketingSettings, PutMarketingSettings, TeamDesignRule } from "@vd/api-client";
import { IconButton, InfoButton, Menu, Surface, Table, TBody, Td, THead, Th, Tr } from "@vd/ui";
import { MoreHorizontal, Plus, Users } from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";
import { qk, usersQuery } from "../../api/admin";
import { errorMessage } from "../../api/client";
import { companyHasEmailMarketing, marketingSettingsQuery, sending, sendingKeys } from "../../api/sending";
import type { Me } from "../../api/types";
import { requireAdmin } from "../../auth/useMe";
import { SettingsGroup, SettingsRow } from "../../components/sending/parts";
import { toast } from "../../components/toast";
import { Button, ErrorState, Input, Loading, PageHeader, Switch } from "../../components/ui";
import { fmtDate } from "../../lib/format";

// Policies > Email marketing (DESIGN.md "Console: Policies > Email marketing", screen k; spec
// 2.15; story EM29), its own sidebar item under Company: designing, the teams' own rules,
// approval, the limits, open tracking and the test-send limits, AI images. Prospects from own
// mailboxes, who can mark people, and Countries wait for their API (EM37).

export const Route = createFileRoute("/admin/email-marketing")({
  staticData: {
    crumb: { label: "Email marketing", parents: [] },
  },
  beforeLoad: (ctx) => {
    requireAdmin(ctx);
    const me = ctx.context.queryClient.getQueryData<Me>(qk.me);
    if (me && !companyHasEmailMarketing(me)) throw redirect({ to: "/admin" });
  },
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.prefetchQuery(marketingSettingsQuery),
      context.queryClient.prefetchQuery(usersQuery),
    ]),
  component: EmailMarketingPolicies,
});

const DESIGN_HELP = "Off: reps pick shared designs and add their own sends.";
const OPENS_HELP = "Opens are an estimate: some mail apps load images for everyone, others never.";
const AI_IMAGES_HELP =
  "The Assistant can make an image when nothing in your brand kit fits. You choose which images to keep.";

function Label({ text, help }: { text: string; help?: string }) {
  return (
    <>
      {text}
      {help ? (
        <InfoButton kind="help" label={`About ${text.toLowerCase()}`}>
          {help}
        </InfoButton>
      ) : null}
    </>
  );
}

function EmailMarketingPolicies() {
  const q = useQuery(marketingSettingsQuery);
  const qc = useQueryClient();
  const put = useMutation({
    mutationFn: (p: PutMarketingSettings) => sending.putSettings(p),
    onMutate: (p) => {
      const prev = qc.getQueryData<MarketingSettings>(sendingKeys.settings);
      if (prev) qc.setQueryData(sendingKeys.settings, { ...prev, ...p });
      return { prev };
    },
    onSuccess: (s) => qc.setQueryData(sendingKeys.settings, s),
    onError: (e, _p, ctx) => {
      if (ctx?.prev) qc.setQueryData(sendingKeys.settings, ctx.prev);
      toast(errorMessage(e), "danger");
    },
  });

  return (
    <>
      <PageHeader title="Email marketing" />
      {q.isPending ? (
        <Loading />
      ) : q.isError ? (
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      ) : (
        <div className="flex max-w-[920px] flex-col gap-6">
          <SettingsGroup title="Designing">
            <SettingsRow label={<Label text="Reps can design emails" help={DESIGN_HELP} />}>
              <Switch
                label="Reps can design emails"
                checked={q.data.reps_can_design}
                onChange={(v) => put.mutate({ reps_can_design: v })}
              />
            </SettingsRow>
          </SettingsGroup>
          <Teams settings={q.data} />
          <SettingsGroup title="Sending">
            <SettingsRow label="Sends need approval">
              <Switch
                label="Sends need approval"
                checked={q.data.approval_required}
                onChange={(v) => put.mutate({ approval_required: v })}
              />
            </SettingsRow>
            <SettingsRow label="Mailbox sends per person, a day">
              <NumberField
                label="Mailbox sends per person, a day"
                value={q.data.mailbox_daily_cap}
                min={1}
                max={500}
                onCommit={(n) => put.mutate({ mailbox_daily_cap: n })}
              />
            </SettingsRow>
            <SettingsRow label="Emails per person, 7 days">
              <NumberField
                label="Emails per person, 7 days"
                value={q.data.weekly_cap}
                min={1}
                max={50}
                onCommit={(n) => put.mutate({ weekly_cap: n })}
              />
            </SettingsRow>
            <SettingsRow label={<Label text="Track opens" help={OPENS_HELP} />}>
              <Switch
                label="Track opens"
                checked={q.data.track_opens}
                onChange={(v) => put.mutate({ track_opens: v })}
              />
            </SettingsRow>
            <SettingsRow label="Test recipients, at most">
              <NumberField
                label="Test recipients, at most"
                value={q.data.test_send_max_recipients}
                min={1}
                max={50}
                onCommit={(n) => put.mutate({ test_send_max_recipients: n })}
              />
            </SettingsRow>
            <SettingsRow label="Test sends per person, an hour">
              <NumberField
                label="Test sends per person, an hour"
                value={q.data.test_sends_per_hour}
                min={1}
                max={500}
                onCommit={(n) => put.mutate({ test_sends_per_hour: n })}
              />
            </SettingsRow>
            <SettingsRow label="Test sends per design, a day">
              <NumberField
                label="Test sends per design, a day"
                value={q.data.test_sends_per_design_day}
                min={1}
                max={500}
                onCommit={(n) => put.mutate({ test_sends_per_design_day: n })}
              />
            </SettingsRow>
          </SettingsGroup>
          <SettingsGroup title="Assistant">
            <SettingsRow
              label={<Label text="AI images" help={AI_IMAGES_HELP} />}
              hint="Brand kit images first. Each image uses credits."
            >
              <Switch
                label="AI images"
                checked={q.data.ai_images}
                onChange={(v) => put.mutate({ ai_images: v })}
              />
            </SettingsRow>
          </SettingsGroup>
        </div>
      )}
    </>
  );
}

/** A number saved when the field loses focus or on Enter, within its range. */
function NumberField({
  label,
  value,
  min,
  max,
  onCommit,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  onCommit: (n: number) => void;
}) {
  const [text, setText] = useState(String(value));
  useEffect(() => setText(String(value)), [value]);
  const n = Number.parseInt(text, 10);
  const valid = Number.isFinite(n) && n >= min && n <= max;
  const commit = () => {
    if (!valid) {
      setText(String(value));
      toast(`${label}: ${min} to ${max}.`, "danger");
      return;
    }
    if (n !== value) onCommit(n);
  };
  return (
    <Input
      aria-label={label}
      className="w-24 text-right tabular-nums"
      inputMode="numeric"
      value={text}
      invalid={!valid}
      onChange={(e) => setText(e.target.value.replace(/\D/g, ""))}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") commit();
      }}
    />
  );
}

function Teams({ settings }: { settings: MarketingSettings }) {
  const qc = useQueryClient();
  const users = useQuery(usersQuery);
  const refresh = () => void qc.invalidateQueries({ queryKey: sendingKeys.settings });
  const fail = (e: unknown) => toast(errorMessage(e), "danger");
  const set = useMutation({
    mutationFn: ({ manager, on }: { manager: string; on: boolean }) => sending.putTeamRule(manager, on),
    onSuccess: refresh,
    onError: fail,
  });
  const remove = useMutation({
    mutationFn: (manager: string) => sending.removeTeamRule(manager),
    onSuccess: refresh,
    onError: fail,
  });
  const ruled = new Set(settings.teams.map((t) => t.manager_user_id));
  const free = (users.data ?? []).filter((u) => u.roles.manager && u.status === "active" && !ruled.has(u.id));
  const add: ReactNode = free.length ? (
    <Menu
      trigger={
        <Button size="sm" variant="ghost">
          <Plus aria-hidden className="size-3.5" />
          Add team rule
        </Button>
      }
      items={free.map((u) => ({
        label: `${u.name}'s team`,
        onSelect: () => set.mutate({ manager: u.id, on: !settings.reps_can_design }),
      }))}
    />
  ) : null;
  return (
    <section aria-label="Teams" className="flex flex-col gap-2">
      <div className="flex min-h-7 items-center justify-between gap-3">
        <h2 className="px-1 font-medium text-caption text-ink-3">Teams</h2>
        {add}
      </div>
      {settings.teams.length ? (
        <Surface radius="xl" className="overflow-hidden">
          <Table>
            <THead>
              <tr>
                <Th>Team</Th>
                <Th>Reps can design</Th>
                <Th>Set by</Th>
                <Th className="w-12">
                  <span className="sr-only">More</span>
                </Th>
              </tr>
            </THead>
            <TBody>
              {settings.teams.map((t) => (
                <TeamRow
                  key={t.manager_user_id}
                  rule={t}
                  onSet={(on) => set.mutate({ manager: t.manager_user_id, on })}
                  onRemove={() => remove.mutate(t.manager_user_id)}
                />
              ))}
            </TBody>
          </Table>
        </Surface>
      ) : (
        <Surface radius="xl" className="flex min-h-12 items-center px-4 text-ink-3 text-ui">
          Every team follows the company setting
        </Surface>
      )}
    </section>
  );
}

function TeamRow({
  rule: t,
  onSet,
  onRemove,
}: {
  rule: TeamDesignRule;
  onSet: (on: boolean) => void;
  onRemove: () => void;
}) {
  return (
    <Tr>
      <Td>
        <span className="flex items-center gap-2 font-medium">
          <Users aria-hidden className="size-3.5 text-ink-3" />
          {t.team}
        </span>
      </Td>
      <Td>
        <Switch label={`${t.team}: reps can design`} checked={t.reps_can_design} onChange={onSet} />
      </Td>
      <Td className="text-caption text-ink-3">
        {t.overridden
          ? [t.set_by_name, t.set_at ? fmtDate(t.set_at) : null].filter(Boolean).join(", ")
          : "Company setting"}
      </Td>
      <Td className="text-right">
        <Menu
          trigger={
            <IconButton size="sm" label={`More for ${t.team}`} icon={<MoreHorizontal aria-hidden />} />
          }
          items={[{ label: "Remove rule", danger: true, onSelect: onRemove }]}
        />
      </Td>
    </Tr>
  );
}
